import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GuideCard } from '../guides/Card';
import { guideText } from '../guides/catalog';
import { placeBeside, type Box } from '../guides/place';

/** How long a hand rests on a tool's button before its guide comes up beside it. */
const HOVER_MS = 900;
/** How long a first-time guide stays when nothing sends it away: a few loops of its picture. */
const INTRO_MS = 10_000;

interface Shown {
  readonly key: string;
  readonly box: Box;
  readonly inMenu: boolean;
  readonly why: 'hover' | 'intro';
  readonly at: number;
}

/**
 * A tool guide beside the control it describes.
 *
 * Two ways in. A long hover on anything that names a guide (`data-guide` — the dock's
 * tools, the rows of its menus, a tool's modes on the strip) brings that tool's guide up
 * next to it, where the help tag would be. And a tool (or mode) picked for the first time
 * in this person's life shows its guide on its own, beside the button that picked it,
 * until they start using it. Option still calls the guide up over the canvas at any time
 * (ToolCursorGuide); pressing it here hands over to that one.
 */
export function GuidePeek({ introduce }: { introduce: { key: string; at: number } | null }) {
  const [shown, setShown] = useState<Shown | null>(null);
  const card = useRef<HTMLDivElement>(null);

  const boxOf = (element: Element): { box: Box; inMenu: boolean } => {
    const r = element.getBoundingClientRect();
    return { box: { left: r.left, top: r.top, width: r.width, height: r.height }, inMenu: !!element.closest('[role="menu"]') };
  };

  // A first pick: beside the control that now stands for it, else above the dock's middle.
  useEffect(() => {
    if (!introduce) return;
    // After the render that put the new tool's button in place — a tick, not a frame,
    // which a hidden tab never gets.
    const frame = setTimeout(() => {
      const control = [...document.querySelectorAll(`[data-guide="${CSS.escape(introduce.key)}"]`)].find((one) => one.getBoundingClientRect().width > 0);
      const fallback: Box = { left: window.innerWidth / 2 - 20, top: window.innerHeight - 64, width: 40, height: 40 };
      const where = control ? boxOf(control) : { box: fallback, inMenu: false };
      setShown({ key: introduce.key, ...where, why: 'intro', at: introduce.at });
    }, 0);
    return () => clearTimeout(frame);
  }, [introduce]);

  useEffect(() => {
    if (shown?.why !== 'intro') return;
    const timer = setTimeout(() => setShown((now) => (now?.at === shown.at ? null : now)), INTRO_MS);
    return () => clearTimeout(timer);
  }, [shown]);

  // A long hover.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let over: Element | null = null;
    const cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const enter = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const control = (event.target as Element | null)?.closest?.('[data-guide]') ?? null;
      if (control === over) return;
      over = control;
      cancel();
      setShown((now) => (now?.why === 'hover' ? null : now));
      if (!control) return;
      timer = setTimeout(() => {
        const key = control.getAttribute('data-guide');
        if (!key || !control.isConnected) return;
        // The guide says more than the help tag, in the same place: the tag steps aside.
        window.dispatchEvent(new Event('artwork:hide-tip'));
        // Already showing this tool's guide (its first-time one): leave it playing.
        setShown((now) => (now?.key === key ? now : { key, ...boxOf(control), why: 'hover', at: performance.now() }));
      }, HOVER_MS);
    };
    const leaveWindow = () => {
      over = null;
      cancel();
      setShown((now) => (now?.why === 'hover' ? null : now));
    };
    // Any press, any key, the window going away: the guide has had its say.
    const dismiss = () => {
      over = null;
      cancel();
      setShown(null);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Shift' || event.key === 'Meta' || event.key === 'Control') return;
      dismiss();
    };
    window.addEventListener('pointerover', enter, true);
    document.documentElement.addEventListener('pointerleave', leaveWindow);
    window.addEventListener('pointerdown', dismiss, true);
    window.addEventListener('keydown', key, true);
    window.addEventListener('blur', dismiss);
    window.addEventListener('resize', dismiss);
    return () => {
      cancel();
      window.removeEventListener('pointerover', enter, true);
      document.documentElement.removeEventListener('pointerleave', leaveWindow);
      window.removeEventListener('pointerdown', dismiss, true);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('blur', dismiss);
      window.removeEventListener('resize', dismiss);
    };
  }, []);

  useLayoutEffect(() => {
    const element = card.current;
    if (!element || !shown) return;
    // The bars and open menus a card could land on — never itself.
    const obstacles = [...document.querySelectorAll('[role="toolbar"], [role="menu"]')]
      .map((one) => one.getBoundingClientRect())
      .filter((r) => r.width > 0 && r.height > 0)
      .map((r) => ({ left: r.left, top: r.top, width: r.width, height: r.height }));
    const at = placeBeside(shown.box, { width: element.offsetWidth, height: element.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }, shown.inMenu, obstacles);
    element.style.transform = `translate(${at.x}px, ${at.y}px)`;
  }, [shown]);

  const text = shown ? guideText(shown.key) : null;
  if (!shown || !text) return null;
  return (
    <div ref={card} className="tool-cursor-guide tool-guide-peek" role="tooltip" data-why={shown.why}>
      <GuideCard key={`${shown.key}@${shown.at}`} guide={shown.key} name={text[0]} line={text[1]} />
    </div>
  );
}
